import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import Fuse from "fuse.js";
import { flatMap, intersection, map, uniq } from "lodash";

import { ArrowLeft, Search } from "@mui/icons-material";
import {
  Box,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemButton,
  ListItemText,
  ListSubheader,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SelectProps,
  TextField,
} from "@mui/material";

import { IAppConfig } from "#/features/User/types";
import { IUnit } from "@/api";
import { useFacilityById, useHealthSystems, useUnitById } from "@/common/AppConfigUtils";

import { CompactFloatedTag } from "../CompactFloatedTag/CompactFloatedTag";
import { Checkbox, MenuItem } from "../TrackedComponents";

import { useHandleQueryParams } from "./hooks";
import "./UnitPickerMobile.scss";

// Types for hierarchy
interface IHierarchyNode {
  id: string;
  name: string;
  parentId?: string;
  type: "market" | "facility" | "unit";
  level: number;
  unitId?: string; // For unit nodes, store the actual unit ID
}

// Type for node selection state
interface INodeSelectionState {
  allSelected: boolean;
  someSelected: boolean;
  unitIds: string[];
}

// Type for render node return value
type RenderNodeReturn = (JSX.Element | null)[];

/**
 * UnitPickerMobile component - Allows selecting units with grouping based on Market -> Facility -> Unit hierarchy
 * This component is used for mobile devices
 */
export const UnitPickerMobile = memo(
  ({
    onChange,
    values,
    valueIds: valueIdsFromParams,
    showFullUnitNames = false,
    size,
    multiple = true,
    unitOptions,
    homeUnitToShowFloatingIcons,
    disableClickOutsideUnit = false,
    haveAllOption: _haveAllOption = false,
    className,
  }: {
    onChange: (selectedUnits: IUnit[]) => void; // Callback when selection changes
    values?: IUnit[]; // Selected units (alternative to valueIds)
    valueIds?: IUnit["id"][]; // IDs of selected units (alternative to values)
    showFullUnitNames?: boolean; // Whether to show full unit names in selection display or hierarchy (like market, facility, etc.)
    size?: SelectProps["size"]; // Size of the select component
    multiple?: boolean; // New prop
    unitOptions?: IUnit[] | IAppConfig["accessibleUnits"]; // Scope down the units to choose from
    homeUnitToShowFloatingIcons?: IUnit["id"]; // Unit ID to not show floating icons for, all the other units will show floating icons
    disableClickOutsideUnit?: boolean; // Disable click outside unit to select
    haveAllOption?: boolean; // Whether to show the "All" option
    className?: string; // Additional CSS class name
  }) => {
    // State - for groups toggle (expanded/collapsed state)
    const [toggledNodes, setToggledNodes] = useState<Record<string, boolean>>({});
    const clickedOnToggle = useRef(false); // Used to detect clicks on toggle vs group selection

    // Add search state
    const [searchQuery, setSearchQuery] = useState("");

    // Get health systems data from app config
    const healthSystems = useHealthSystems();
    const facilityById = useFacilityById();
    const unitById = useUnitById();

    // Normalize valueIds from either values prop or valueIds prop
    const valueIds = useMemo(
      () => valueIdsFromParams || map(values, "id"),
      [values, valueIdsFromParams],
    );

    // Generate a flat list of all units from health systems, indexed by ID for quick lookup
    const allUnitsMap = useMemo(() => {
      const possibleUnitMap = new Map<string, IUnit>();

      // Extract units from the nested structure
      healthSystems.forEach((healthSystem) => {
        if (!healthSystem.archivedAt) {
          healthSystem.regions?.forEach((region) => {
            if (!region.archivedAt) {
              region.markets?.forEach((market) => {
                if (!market.archivedAt) {
                  market.facilityIds?.forEach((facilityId) => {
                    if (facilityById[facilityId]?.archivedAt) return;

                    facilityById[facilityId]?.unitIds?.forEach((unitId) => {
                      const unit = unitById[unitId];
                      if (!unit || unit.archivedAt) return;

                      possibleUnitMap.set(unitId, unit);
                    });
                  });
                }
              });
            }
          });
        }
      });

      let finalUnitMap = new Map<string, IUnit>();

      if (unitOptions) {
        unitOptions.forEach((unit) => {
          if (!unit.archivedAt) {
            const possibleUnit = possibleUnitMap.get(unit.id);
            if (possibleUnit) {
              finalUnitMap.set(unit.id, possibleUnit);
            }
          }
        });
      } else {
        finalUnitMap = possibleUnitMap;
      }

      return finalUnitMap;
    }, [facilityById, healthSystems, unitById, unitOptions]);

    // Create hierarchical node structure
    const hierarchyNodes = useMemo(() => {
      const nodes: IHierarchyNode[] = [];

      // Check if all units belong to the same facility
      const allFacilityIds = Array.from(allUnitsMap.values()).map((unit) => unit.facilityId);
      const uniqueFacilityIds = uniq(allFacilityIds);
      const hasSingleFacility = uniqueFacilityIds.length === 1;

      // Transform the nested structure into a flat list of nodes with parent references
      healthSystems.forEach((healthSystem) => {
        healthSystem.regions?.forEach((region) => {
          region.markets?.forEach((market) => {
            const marketUnits: IHierarchyNode[] = [];
            const marketFacilities: IHierarchyNode[] = [];

            market.facilityIds?.forEach((facilityId) => {
              const facilityUnits: IHierarchyNode[] = [];

              const facility = facilityById[facilityId];
              if (!facility || facility.archivedAt) return;

              facility.unitIds?.forEach((unitId) => {
                // Only add unit if it's not archived and is in the allUnitsMap
                const unit = unitById[unitId];
                if (!unit || unit.archivedAt || !allUnitsMap.has(unit.id)) return;

                facilityUnits.push({
                  id: `unit_${unit.id}`,
                  name: unit.name,
                  parentId: `facility_${facility.id}`,
                  type: "unit",
                  level: hasSingleFacility ? 1 : 2,
                  unitId: unit.id,
                });
              });

              // Only add facility if it has units
              if (facilityUnits.length > 0) {
                const facilityNode: IHierarchyNode = {
                  id: `facility_${facility.id}`,
                  name: facility.name,
                  parentId: hasSingleFacility ? undefined : `market_${market.id}`,
                  type: "facility",
                  level: hasSingleFacility ? 0 : 1,
                };
                marketFacilities.push(facilityNode);
                marketUnits.push(...facilityUnits);
              }
            });

            // Only add market and its children if it has units and we're not in single facility mode
            if (marketUnits.length > 0 && !hasSingleFacility) {
              const marketNode: IHierarchyNode = {
                id: `market_${market.id}`,
                name: market.name,
                type: "market",
                level: 0,
              };
              nodes.push(marketNode);
              nodes.push(...marketFacilities);
              nodes.push(...marketUnits);
            } else if (marketUnits.length > 0 && hasSingleFacility) {
              // In single facility mode, just add the facilities and their units
              nodes.push(...marketFacilities);
              nodes.push(...marketUnits);
            }
          });
        });
      });

      return nodes;
    }, [facilityById, healthSystems, unitById, allUnitsMap]);

    // Create options array for the select component
    const options = useMemo(
      () =>
        hierarchyNodes
          .filter((node) => node.type === "unit")
          .map((node) => ({
            label: node.name,
            value: node.unitId!,
          })),
      [hierarchyNodes],
    );

    // Get node by ID for quick lookup
    const nodesByIdMap = useMemo(() => {
      return hierarchyNodes.reduce(
        (acc, node) => {
          acc[node.id] = node;
          return acc;
        },
        {} as Record<string, IHierarchyNode>,
      );
    }, [hierarchyNodes]);

    // Get child nodes for a given parent ID
    const getChildNodes = useCallback(
      (parentId: string) => {
        return hierarchyNodes.filter((node) => node.parentId === parentId);
      },
      [hierarchyNodes],
    );

    // Get unit IDs for a given parent node (recursively)
    const getUnitIdsForNode = useCallback(
      (nodeId: string): string[] => {
        const node = nodesByIdMap[nodeId];
        if (!node) return [];

        if (node.type === "unit" && node.unitId) {
          return [node.unitId];
        } else {
          // Get all children nodes
          const childNodes = getChildNodes(nodeId);
          // Recursively get unit IDs for all children
          return flatMap(childNodes, (child) => getUnitIdsForNode(child.id));
        }
      },
      [nodesByIdMap, getChildNodes],
    );

    // Filter valueIds to only include available units
    const selectedValueIds = useMemo(
      () => intersection(valueIds, Array.from(allUnitsMap.keys())),
      [allUnitsMap, valueIds],
    );

    // Calculate which nodes are fully or partially selected
    const nodeSelectionState = useMemo(() => {
      const result: Record<string, INodeSelectionState> = {};

      hierarchyNodes.forEach((node) => {
        if (node.type === "unit") {
          const isSelected = node.unitId ? selectedValueIds.includes(node.unitId) : false;
          result[node.id] = {
            allSelected: isSelected,
            someSelected: isSelected,
            unitIds: node.unitId ? [node.unitId] : [],
          };
        } else {
          const unitIds = getUnitIdsForNode(node.id);
          const selectedUnitIds = intersection(unitIds, selectedValueIds);

          result[node.id] = {
            allSelected: selectedUnitIds.length === unitIds.length && unitIds.length > 0,
            someSelected: selectedUnitIds.length > 0,
            unitIds,
          };
        }
      });

      return result;
    }, [hierarchyNodes, selectedValueIds, getUnitIdsForNode]);

    // Auto-expand the first node that has a selected unit on initial render
    useEffect(() => {
      if (Object.keys(toggledNodes).length > 0) return; // Skip if nodes are already toggled

      // Find the first market with selected units
      const firstSelectedMarket = hierarchyNodes.find(
        (node) => node.type === "market" && nodeSelectionState[node.id]?.someSelected,
      );

      if (firstSelectedMarket) {
        // Find facilities under this market with selected units
        const facilitiesWithSelected = getChildNodes(firstSelectedMarket.id).filter(
          (facility) => nodeSelectionState[facility.id]?.someSelected,
        );

        const newToggledNodes = { [firstSelectedMarket.id]: true };

        // Add facilities to toggled nodes
        facilitiesWithSelected.forEach((facility) => {
          newToggledNodes[facility.id] = true;
        });

        setToggledNodes(newToggledNodes);
      }
    }, [hierarchyNodes, nodeSelectionState, getChildNodes, toggledNodes]);

    // Create Fuse instance for fuzzy search
    const fuse = useMemo(() => {
      const searchableNodes = hierarchyNodes.map((node) => ({
        id: node.id,
        name: node.name,
        type: node.type,
        unitId: node.unitId,
        parentId: node.parentId,
      }));

      return new Fuse(searchableNodes, {
        keys: ["name"],
        threshold: 0.3,
        ignoreLocation: true,
      });
    }, [hierarchyNodes]);

    // Filter nodes based on search query
    const filteredNodes = useMemo(() => {
      if (!searchQuery.trim()) return hierarchyNodes;

      const results = fuse.search(searchQuery);
      const matchedNodeIds = new Set(results.map((result) => result.item.id));

      // Include parent nodes of matched nodes
      const relevantNodeIds = new Set(matchedNodeIds);
      results.forEach((result) => {
        let currentNode = nodesByIdMap[result.item.id];
        while (currentNode?.parentId) {
          relevantNodeIds.add(currentNode.parentId);
          currentNode = nodesByIdMap[currentNode.parentId];
        }
      });

      return hierarchyNodes.filter((node) => relevantNodeIds.has(node.id));
    }, [searchQuery, fuse, hierarchyNodes, nodesByIdMap]);

    // Auto-expand nodes when searching
    useEffect(() => {
      if (!searchQuery.trim()) return;

      const results = fuse.search(searchQuery);
      const matchedNodeIds = new Set(results.map((result) => result.item.id));

      // Include parent nodes of matched nodes
      const relevantNodeIds = new Set(matchedNodeIds);
      results.forEach((result) => {
        let currentNode = nodesByIdMap[result.item.id];
        while (currentNode?.parentId) {
          relevantNodeIds.add(currentNode.parentId);
          currentNode = nodesByIdMap[currentNode.parentId];
        }
      });

      // Auto-expand nodes that have matches
      const newToggledNodes = { ...toggledNodes };
      relevantNodeIds.forEach((nodeId) => {
        const node = nodesByIdMap[nodeId];
        if (node && node.type !== "unit") {
          newToggledNodes[nodeId] = true;
        }
      });
      setToggledNodes(newToggledNodes);
    }, [searchQuery, fuse, nodesByIdMap, toggledNodes]);

    // Highlight search matches in node names
    const highlightSearchMatch = useCallback(
      (text: string) => {
        if (!searchQuery.trim()) return text;

        const parts = text.split(new RegExp(`(${searchQuery})`, "gi"));
        return (
          <span>
            {parts.map((part, i) =>
              part.toLowerCase() === searchQuery.toLowerCase() ? (
                <span
                  key={i}
                  style={{ backgroundColor: "rgba(0, 0, 0, 0.1)", borderRadius: "2px" }}
                >
                  {part}
                </span>
              ) : (
                part
              ),
            )}
          </span>
        );
      },
      [searchQuery],
    );

    // Event handlers

    // Handle individual unit selection from the dropdown
    const onUnitSelect = useCallback(
      (event: SelectChangeEvent<unknown>) => {
        if (!multiple) {
          const newValue = event.target.value as string;
          const selectedUnit = allUnitsMap.get(newValue);
          onChange(selectedUnit ? [selectedUnit] : []);
          return;
        }

        const newSelectedUnitIds = event.target.value as string[];

        // Handle "all" selection - if all is selected and it's the only selected option, then clear selection
        if (newSelectedUnitIds.includes("all")) {
          return;
        }

        // Handle unit selections
        const selectedUnits = newSelectedUnitIds
          .map((id) => allUnitsMap.get(id))
          .filter(Boolean) as IUnit[];

        onChange(selectedUnits);
      },
      [allUnitsMap, onChange, multiple],
    );

    // Handle selecting/deselecting a node (market, facility, or unit)
    const onNodeSelect = useCallback(
      (nodeId: string) => {
        const node = nodesByIdMap[nodeId];
        if (!node) return;

        // If disableClickOutsideUnit is true, only allow selecting units
        if (disableClickOutsideUnit && node.type !== "unit") {
          return;
        }

        const { allSelected, unitIds } = nodeSelectionState[nodeId] || {
          allSelected: false,
          unitIds: [],
        };

        // If all units in node are selected, remove them; otherwise add them
        const updatedIdSelection = allSelected
          ? selectedValueIds.filter((unitId) => !unitIds.includes(unitId))
          : uniq([...selectedValueIds, ...unitIds]);
        // Convert IDs back to unit objects
        let selectedUnits = updatedIdSelection
          .map((id) => allUnitsMap.get(id))
          .filter(Boolean) as IUnit[];

        // If all units are selected, clear selection
        if (selectedUnits.length === allUnitsMap.size) {
          selectedUnits = [];
        }

        onChange(selectedUnits);
      },
      [
        allUnitsMap,
        nodeSelectionState,
        nodesByIdMap,
        onChange,
        selectedValueIds,
        disableClickOutsideUnit,
      ],
    );

    // Add this helper function
    const getFloatingIconForUnit = (unitId: string | undefined, homeUnitId: string | undefined) => {
      if (!unitId || !homeUnitId || unitId === homeUnitId) {
        return null;
      }
      return <CompactFloatedTag unitId={homeUnitId} grayIfNotFloatedStatus />;
    };

    // Render a node (market, facility, or unit) with toggle button if needed
    const renderNode = useCallback(
      (node: IHierarchyNode): RenderNodeReturn => {
        const selectionState = nodeSelectionState[node.id] || {
          allSelected: false,
          someSelected: false,
        };
        const isGroup = node.type !== "unit";

        if (isGroup) {
          const isToggled = !!toggledNodes[node.id];
          const toggledClass = isToggled ? "toggled" : "";

          const childNodes = getChildNodes(node.id).filter((child) =>
            filteredNodes.some((filteredNode) => filteredNode.id === child.id),
          );
          const result: RenderNodeReturn = [];

          // Add the group header
          result.push(
            <ListSubheader
              key={node.id}
              className="li-group-selector"
              data-level={node.level}
              sx={{ pl: node.level * 2 }}
              onClickCapture={(event) => {
                // If disableClickOutsideUnit is true, prevent group selection
                if (disableClickOutsideUnit) {
                  event.stopPropagation();
                  return;
                }
                setTimeout(() => {
                  if (clickedOnToggle.current) return (clickedOnToggle.current = false);
                  onNodeSelect(node.id);
                });
              }}
            >
              <Checkbox
                indeterminate={selectionState.someSelected && !selectionState.allSelected}
                checked={selectionState.allSelected}
                trackingLabel={`select-${node.type}`}
                style={{
                  display: multiple ? "inline-flex" : "none",
                  opacity: disableClickOutsideUnit ? 0.5 : 1,
                  pointerEvents: disableClickOutsideUnit ? "none" : "auto",
                }}
              />
              <ListItemText primary={highlightSearchMatch(node.name)} />
              <ListItemButton
                className="toggle-group-button"
                onClick={() => {
                  clickedOnToggle.current = true;
                  const newToggledState = !toggledNodes[node.id];
                  setToggledNodes((prev) => ({
                    ...prev,
                    [node.id]: newToggledState,
                  }));
                  // Clear search when closing a layer
                  if (!newToggledState) {
                    setSearchQuery("");
                  }
                }}
              >
                <ArrowLeft className={`toggle-group-icon ${toggledClass}`} />
              </ListItemButton>
            </ListSubheader>,
          );

          // Add child nodes
          childNodes.forEach((childNode) => {
            const hidden = !isToggled;

            if (childNode.type === "unit") {
              // Render unit as a menu item
              result.push(
                <MenuItem
                  trackingLabel={null}
                  key={childNode.id}
                  value={childNode.unitId}
                  data-level={childNode.level}
                  sx={{
                    pl: (childNode.level + 1) * 2,
                    ...(hidden ? { display: "none" } : {}),
                  }}
                  className="m7-unit-group-picker-menu-item"
                >
                  <Checkbox
                    checked={childNode.unitId ? selectedValueIds.includes(childNode.unitId) : false}
                    trackingLabel="select-unit"
                    style={{ display: multiple ? "inline-flex" : "none" }}
                  />
                  <ListItemText primary={highlightSearchMatch(childNode.name)} />
                  {getFloatingIconForUnit(childNode.unitId, homeUnitToShowFloatingIcons)}
                </MenuItem>,
              );
            } else if (!hidden) {
              // Recursively render child groups if not hidden
              const childElements = renderNode(childNode);
              result.push(...childElements);
            }
          });

          return result;
        }

        // Unit nodes remain clickable
        return [
          <MenuItem
            key={node.id}
            value={node.unitId || ""}
            sx={{ pl: node.level * 2 }}
            trackingLabel={"unit-node-in-hierarchy-unit-picker"}
          >
            <ListItemText primary={highlightSearchMatch(node.name)} />
            {node.unitId && getFloatingIconForUnit(node.unitId, homeUnitToShowFloatingIcons)}
          </MenuItem>,
        ];
      },
      [
        nodeSelectionState,
        toggledNodes,
        onNodeSelect,
        filteredNodes,
        getChildNodes,
        homeUnitToShowFloatingIcons,
        multiple,
        selectedValueIds,
        disableClickOutsideUnit,
        highlightSearchMatch,
      ],
    );

    // Render the selected values as chips in the select input
    const renderSelectedValues = useCallback(
      (selected: unknown) => {
        if (!multiple) {
          const unitId = selected as string;
          const unit = allUnitsMap.get(unitId);
          return unit ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {unit.name}
              {getFloatingIconForUnit(unit.id, homeUnitToShowFloatingIcons)}
            </Box>
          ) : null;
        }

        const selectedArray = selected as string[];

        if (showFullUnitNames) {
          return (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
              }}
              className={className}
            >
              {selectedArray.map((unitId) => {
                const unit = allUnitsMap.get(unitId);
                return unit ? (
                  <Chip
                    key={unit.id}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {unit.name}
                        {getFloatingIconForUnit(unit.id, homeUnitToShowFloatingIcons)}
                      </Box>
                    }
                    size="small"
                  />
                ) : null;
              })}
            </Box>
          );
        }

        // Otherwise, group by market and facility and show counts
        const chips: { label: string; key: string }[] = [];

        // Find markets with fully selected facilities/units
        const marketsWithSelection = hierarchyNodes.filter(
          (node) => node.type === "market" && nodeSelectionState[node.id]?.someSelected,
        );

        // For each market, check selection status
        marketsWithSelection.forEach((market) => {
          const marketState = nodeSelectionState[market.id];

          if (marketState?.allSelected) {
            // If all units in market are selected, show the market
            chips.push({
              key: market.id,
              label: `${market.name} (${marketState.unitIds.length})`,
            });
          } else {
            // For partially selected markets, show facilities with selections
            const facilitiesWithSelection = getChildNodes(market.id).filter(
              (node) => nodeSelectionState[node.id]?.someSelected,
            );

            facilitiesWithSelection.forEach((facility) => {
              const facilityState = nodeSelectionState[facility.id];

              if (facilityState?.allSelected) {
                // If all units in facility are selected, show the facility
                chips.push({
                  key: facility.id,
                  label: `${facility.name} (${facilityState.unitIds.length})`,
                });
              } else {
                // For partially selected facilities, show unit count
                const selectedUnitCount = intersection(
                  facilityState?.unitIds || [],
                  selectedValueIds,
                ).length;
                if (selectedUnitCount > 0) {
                  chips.push({
                    key: facility.id,
                    label: `${facility.name} (${selectedUnitCount})`,
                  });
                }
              }
            });
          }
        });

        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
            className={`m7-unit-group-picker-chips ${className || ""}`}
          >
            {chips.map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                size="small"
                className="m7-unit-group-picker-chip"
              />
            ))}
          </Box>
        );
      },
      [
        allUnitsMap,
        showFullUnitNames,
        homeUnitToShowFloatingIcons,
        multiple,
        hierarchyNodes,
        nodeSelectionState,
        getChildNodes,
        selectedValueIds,
        className,
      ],
    );

    // Get root level (market) nodes to render first
    const rootNodes = useMemo(() => {
      // Check if all units belong to the same facility
      const allFacilityIds = Array.from(allUnitsMap.values()).map((unit) => unit.facilityId);
      const uniqueFacilityIds = uniq(allFacilityIds);
      const hasSingleFacility = uniqueFacilityIds.length === 1;

      if (hasSingleFacility) {
        // In single facility mode, use facilities as root nodes
        return hierarchyNodes.filter((node) => node.type === "facility");
      }
      // Otherwise use markets as root nodes
      return hierarchyNodes.filter((node) => node.type === "market");
    }, [hierarchyNodes, allUnitsMap]);

    // After all defined, handle URL query params synchronization
    useHandleQueryParams({
      onChange,
      valueIds,
      withQueryParams: false, // Set to true if you want to enable URL param syncing
      nodeSelectionState,
      hierarchyNodes,
      options,
      isLoading: false,
      allUnitsMap,
    });

    // Render the complete component
    return (
      <FormControl
        className={`m7-unit-group-picker-mobile ${className || ""}`}
        sx={{ width: 300 }}
        size={size}
      >
        <InputLabel id="unit-selector-label">{multiple ? "Units" : "Unit"}</InputLabel>
        <Select
          multiple={multiple}
          value={multiple ? selectedValueIds : selectedValueIds[0] || ""}
          onChange={onUnitSelect}
          input={<OutlinedInput label={multiple ? "Units" : "Unit"} />}
          renderValue={renderSelectedValues}
          id="unit-selector"
          MenuProps={{
            className: "m7-unit-group-picker-menu-mobile",
            // Add search field to menu props
            PaperProps: {
              sx: {
                "& .MuiList-root": {
                  paddingTop: 0,
                },
              },
            },
          }}
        >
          {/* Search input */}
          <Box
            component="div"
            sx={{
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 1,
              p: 1,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search units..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            />
          </Box>

          {/* "All" option at the top */}
          {/* FOR NOW: No All Option Ever */}
          {/* {multiple && haveAllOption && !disableClickOutsideUnit && (
            <MenuItem
              trackingLabel={null}
              key={"all"}
              value={"all"}
              onClick={() => {
                onChange([]);
              }}
            >
              <Checkbox
                checked={selectedValueIds.length === 0}
                trackingLabel="select-all-units"
                style={{ display: multiple ? "inline-flex" : "none" }}
              />
              <ListItemText primary={"All"} />
            </MenuItem>
          )} */}

          {/* Render the hierarchical structure with filtered nodes */}
          {rootNodes
            .filter((node) => filteredNodes.some((filteredNode) => filteredNode.id === node.id))
            .map((node) => renderNode(node))}
        </Select>
      </FormControl>
    );
  },
);
